import { useCallback, useEffect, useRef, useState } from 'react';
import type { AuthClient, AuthProviderId, AuthSession } from './provider.js';

export interface UseAuth {
  /** The signed-in session, or null when signed out / still resolving. */
  session: AuthSession | null;
  /** False until the first auth-state callback fires (avoids a sign-in flash on load). */
  ready: boolean;
  /** Whether real auth is configured (else sign-in is disabled / local-only). */
  configured: boolean;
  signIn: (provider: AuthProviderId) => Promise<void>;
  signOut: () => Promise<void>;
}

/**
 * Subscribe a React tree to an AuthClient. The client is created once by the app (Firebase or
 * local-only) and passed in, so this hook is pure state-tracking — testable with FakeAuthClient.
 */
export function useAuth(client: AuthClient): UseAuth {
  const [session, setSession] = useState<AuthSession | null>(null);
  const [ready, setReady] = useState(false);
  // Keep the latest session reachable from stable callbacks without re-subscribing.
  const sessionRef = useRef<AuthSession | null>(null);

  useEffect(() => {
    return client.onChange((s) => {
      sessionRef.current = s;
      setSession(s);
      setReady(true);
    });
  }, [client]);

  const signIn = useCallback((provider: AuthProviderId) => client.signIn(provider), [client]);
  const signOut = useCallback(() => client.signOut(), [client]);

  return { session, ready, configured: client.configured, signIn, signOut };
}
